import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, CalendarDays, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md rounded-2xl">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <AlertTriangle strokeWidth={1.5} className="size-10 text-destructive mx-auto" />
            <h1 className="font-display text-2xl font-bold">Invalid reset link</h1>
            <p className="text-sm text-muted-foreground">
              This link is missing the reset token. Request a new one.
            </p>
            <Button asChild className="rounded-xl"><Link to="/forgot-password">Request new link</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setError("Password must contain at least one letter and one number.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset. Sign in with your new password.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md rounded-2xl">
        {done ? (
          <CardContent className="pt-8 pb-8 text-center space-y-4" data-testid="reset-success">
            <div className="size-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto">
              <CheckCircle2 strokeWidth={1.5} className="size-7 text-primary" />
            </div>
            <h1 className="font-display text-2xl font-bold">Password reset</h1>
            <p className="text-sm text-muted-foreground">Redirecting you to sign in…</p>
          </CardContent>
        ) : (
          <>
            <CardHeader className="space-y-3">
              <div className="flex items-center gap-2 text-primary">
                <CalendarDays strokeWidth={1.5} className="size-7" />
                <span className="font-display font-extrabold text-2xl tracking-tight">Lumivera</span>
              </div>
              <CardTitle className="font-display text-3xl">Choose a new password</CardTitle>
              <CardDescription>
                At least 8 characters, with at least one letter and one number.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
                <div className="space-y-2">
                  <Label htmlFor="password">New password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder="Min 8 chars, with a letter and a number"
                      data-testid="reset-password-input"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="reset-toggle-password"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff strokeWidth={1.5} className="size-4" /> : <Eye strokeWidth={1.5} className="size-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirm new password</Label>
                  <Input
                    id="confirm"
                    type={showPassword ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    placeholder="••••••••"
                    data-testid="reset-confirm-input"
                  />
                </div>
                {error && <div className="text-sm text-destructive" data-testid="reset-error">{error}</div>}
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-xl h-11 text-base font-semibold"
                  data-testid="reset-submit-button"
                >
                  <ShieldCheck strokeWidth={1.5} className="mr-2 size-4" />
                  {busy ? "Updating…" : "Update password"}
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
